import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';

const REACTION_TYPES = [
  { type: 'fire',    emoji: '🔥', label: 'This hard',  weight: 5 },
  { type: 'mic',     emoji: '🎤', label: 'Say that',   weight: 5 },
  { type: 'rocket',  emoji: '🚀', label: 'Going up',   weight: 10 },
  { type: 'diamond', emoji: '💎', label: 'Elite',       weight: 20 },
  { type: 'laugh',   emoji: '😂', label: 'Funny',       weight: 3 },
  { type: 'bolt',    emoji: '⚡', label: 'Turn up',     weight: 8 },
];

const COOLDOWN_MS = 900;
const POLL_INTERVAL = 2500;

export function useLiveReactions(streamId) {
  const { getAuthHeaders, isAuthenticated } = useAuth();
  const [energyState, setEnergyState] = useState({ energy: 0, energy_max: 1000, energy_pct: 0, level: 'low', label: 'Low Energy', triggered_max: false });
  const [reactionFeed, setReactionFeed] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [moments, setMoments] = useState([]);
  const [cooldowns, setCooldowns] = useState({});
  const [crowdGoingCrazy, setCrowdGoingCrazy] = useState(false);
  const pollRef = useRef(null);
  const prevLevelRef = useRef('low');

  const fetchState = useCallback(async () => {
    if (!streamId) return;
    try {
      const headers = isAuthenticated ? getAuthHeaders() : {};
      const [stateRes, feedRes, lbRes] = await Promise.all([
        fetch(`${API}/live/streams/${streamId}/reactions/state`, { headers }),
        fetch(`${API}/live/streams/${streamId}/reactions/feed?limit=15`, { headers }),
        fetch(`${API}/live/streams/${streamId}/reactions/leaderboard?limit=10`, { headers }),
      ]);
      if (stateRes.ok) {
        const s = await stateRes.json();
        setEnergyState(s);
        // Detect level transition to max
        if (s.level === 'max' && prevLevelRef.current !== 'max') {
          setCrowdGoingCrazy(true);
          setTimeout(() => setCrowdGoingCrazy(false), 5000);
        }
        prevLevelRef.current = s.level;
      }
      if (feedRes.ok) { const f = await feedRes.json(); setReactionFeed(f.reactions || []); }
      if (lbRes.ok) { const l = await lbRes.json(); setLeaderboard(l.supporters || []); }
    } catch (e) { /* silent */ }
  }, [streamId, isAuthenticated]);

  useEffect(() => {
    if (!streamId) return;
    fetchState();
    pollRef.current = setInterval(fetchState, POLL_INTERVAL);
    return () => clearInterval(pollRef.current);
  }, [streamId, fetchState]);

  const sendReaction = useCallback(async (reactionType, isSuper = false) => {
    if (!isAuthenticated || !streamId) return null;
    // Client-side cooldown
    const now = Date.now();
    if (cooldowns[reactionType] && now - cooldowns[reactionType] < COOLDOWN_MS) return null;
    setCooldowns(prev => ({ ...prev, [reactionType]: now }));

    try {
      const res = await fetch(`${API}/live/streams/${streamId}/reactions`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ reaction_type: reactionType, is_super: isSuper }),
      });
      if (res.ok) {
        const data = await res.json();
        // Immediately update energy
        setEnergyState(prev => ({
          ...prev,
          energy: data.energy,
          energy_pct: Math.round(data.energy / 1000 * 100 * 10) / 10,
          level: data.energy_level,
          label: data.energy_label,
          triggered_max: data.triggered_max,
        }));
        if (data.triggered_max) {
          setCrowdGoingCrazy(true);
          setTimeout(() => setCrowdGoingCrazy(false), 5000);
        }
        return data;
      }
    } catch (e) { /* silent */ }
    return null;
  }, [streamId, isAuthenticated, cooldowns]);

  const fetchMoments = useCallback(async () => {
    if (!streamId) return;
    try {
      const res = await fetch(`${API}/live/streams/${streamId}/reactions/moments`);
      if (res.ok) { const m = await res.json(); setMoments(m.moments || []); }
    } catch (e) { /* silent */ }
  }, [streamId]);

  return {
    REACTION_TYPES,
    energyState,
    reactionFeed,
    leaderboard,
    moments,
    crowdGoingCrazy,
    sendReaction,
    fetchMoments,
    cooldowns,
  };
}
